/**
 * Logger Utility
 *
 * Centralized logging system with log levels and environment-based controls.
 * Replaces direct console.log usage with a more robust logging solution.
 *
 * Usage:
 *   import logger from '../utils/logger';
 *   logger.debug('Component', 'Debug message', { data });
 *   logger.info('Component', 'Info message');
 *   logger.warn('Component', 'Warning message');
 *   logger.error('Component', 'Error message', error);
 */

// Log levels
const LOG_LEVELS = {
    DEBUG: 0,
    INFO: 1,
    WARN: 2,
    ERROR: 3,
    NONE: 4
};

// Configuration
const config = {
    // Set minimum log level based on environment
    minLevel: process.env.NODE_ENV === 'production'
        ? LOG_LEVELS.WARN  // Production: only warnings and errors
        : LOG_LEVELS.DEBUG, // Development: all logs

    // Enable/disable specific components
    enabledComponents: process.env.NODE_ENV === 'production'
        ? [] // Production: no component logs
        : ['*'], // Development: all components (use '*' for all, or specify ['DirectMessaging', 'DMInbox'])

    // Add timestamps to logs
    timestamps: process.env.NODE_ENV === 'development',

    // Colorize console output
    colors: process.env.NODE_ENV === 'development',

    // Send logs to external service (e.g., Sentry, LogRocket)
    externalLogging: false
};

// Color codes for different log levels
const COLORS = {
    DEBUG: '#9CA3AF',   // Gray
    INFO: '#3B82F6',    // Blue
    WARN: '#F59E0B',    // Amber
    ERROR: '#EF4444',   // Red
    COMPONENT: '#8B5CF6' // Purple
};

/**
 * Check if logging is enabled for a component
 */
function isComponentEnabled(component) {
    if (!component) return true;

    // If '*' is in enabled components, allow all
    if (config.enabledComponents.includes('*')) return true;

    // Check if specific component is enabled
    return config.enabledComponents.includes(component);
}

/**
 * Apply color styling to console output
 */
function styleLog(level, component, message, ...args) {
    if (!config.colors) {
        return [message, ...args];
    }

    // Build styled output
    const styles = [];
    let formattedMessage = '';

    if (config.timestamps) {
        formattedMessage += '%c[%s]%c ';
        styles.push('color: #6B7280', new Date().toLocaleTimeString('en-US', {
            hour12: false,
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            fractionalSecondDigits: 3
        }), 'color: inherit');
    }

    if (component) {
        formattedMessage += `%c[${component}]%c `;
        styles.push(`color: ${COLORS.COMPONENT}; font-weight: bold`, 'color: inherit');
    }

    formattedMessage += `%c${message}%c`;
    styles.push(`color: ${COLORS[level]}`, 'color: inherit');

    return [formattedMessage, ...styles, ...args];
}

/**
 * Send log to external service
 */
function sendToExternalService(level, component, message, data) {
    if (!config.externalLogging) return;

    // Integration point for external logging services
    if (window.logErrorToService && level === 'ERROR') {
        window.logErrorToService({
            level,
            component,
            message,
            data,
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
            url: window.location.href
        });
    }
}

/**
 * Core logging function
 */
function log(level, levelName, component, message, ...args) {
    // Check if this log level should be shown
    if (level < config.minLevel) return;

    // Check if this component's logs are enabled
    if (!isComponentEnabled(component)) return;

    // Extract error object if present
    const data = args.length > 0 ? args[0] : undefined;

    // Send to external service if configured
    sendToExternalService(levelName, component, message, data);

    // Output to console
    const consoleMethod = levelName === 'ERROR' ? 'error'
                        : levelName === 'WARN' ? 'warn'
                        : levelName === 'INFO' ? 'info'
                        : 'log';

    console[consoleMethod](...styleLog(levelName, component, message, ...args));
}

/**
 * Logger object with methods for each log level
 */
const logger = {
    /**
     * Debug level - detailed information for debugging
     * @param {string} component - Component or module name
     * @param {string} message - Log message
     * @param {...any} args - Additional arguments
     */
    debug(component, message, ...args) {
        log(LOG_LEVELS.DEBUG, 'DEBUG', component, message, ...args);
    },

    /**
     * Info level - general informational messages
     * @param {string} component - Component or module name
     * @param {string} message - Log message
     * @param {...any} args - Additional arguments
     */
    info(component, message, ...args) {
        log(LOG_LEVELS.INFO, 'INFO', component, message, ...args);
    },

    /**
     * Warn level - warning messages for potentially harmful situations
     * @param {string} component - Component or module name
     * @param {string} message - Log message
     * @param {...any} args - Additional arguments
     */
    warn(component, message, ...args) {
        log(LOG_LEVELS.WARN, 'WARN', component, message, ...args);
    },

    /**
     * Error level - error messages
     * @param {string} component - Component or module name
     * @param {string} message - Log message
     * @param {...any} args - Additional arguments (typically Error object)
     */
    error(component, message, ...args) {
        log(LOG_LEVELS.ERROR, 'ERROR', component, message, ...args);
    },

    /**
     * Configure logger settings
     * @param {Object} newConfig - Configuration object
     */
    configure(newConfig) {
        Object.assign(config, newConfig);
    },

    /**
     * Get current configuration
     */
    getConfig() {
        return { ...config };
    },

    /**
     * Enable logging for specific components
     * @param {...string} components - Component names to enable
     */
    enable(...components) {
        config.enabledComponents = [...new Set([...config.enabledComponents, ...components])];
    },

    /**
     * Disable logging for specific components
     * @param {...string} components - Component names to disable
     */
    disable(...components) {
        config.enabledComponents = config.enabledComponents.filter(
            c => !components.includes(c)
        );
    },

    /**
     * Enable all logging
     */
    enableAll() {
        config.enabledComponents = ['*'];
    },

    /**
     * Disable all logging
     */
    disableAll() {
        config.enabledComponents = [];
    }
};

// Freeze the logger object to prevent modification
Object.freeze(logger);

export default logger;

// Export LOG_LEVELS for external use
export { LOG_LEVELS };
